import React, { useState, useEffect } from 'react';
import { Mail, Plus, Edit2, Trash2, Save, X, Eye, Loader2, AlertCircle, CheckCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { adminApi } from '@/lib/api';

const TEMPLATE_TYPES = {
  ACKNOWLEDGMENT: { label: 'Confirmação de Receção', color: 'bg-blue-100 text-blue-800' },
  STATUS_UPDATE: { label: 'Atualizacao Estado', color: 'bg-green-100 text-green-800' },
  DEADLINE_ALERT: { label: 'Alerta Prazo', color: 'bg-red-100 text-red-800' },
  MFA_ENABLED: { label: 'MFA Ativado', color: 'bg-purple-100 text-purple-800' },
  MFA_DISABLED: { label: 'MFA Desativado', color: 'bg-orange-100 text-orange-800' },
  WORKFLOW_APPROVAL: { label: 'Aprovacao Workflow', color: 'bg-amber-100 text-amber-800' },
  CUSTOM: { label: 'Personalizado', color: 'bg-slate-100 text-slate-800' }
};

const AVAILABLE_VARIABLES = [
  { var: '{{tracking_code}}', desc: 'Codigo de seguimento' },
  { var: '{{status}}', desc: 'Estado da denuncia' },
  { var: '{{deadline_type}}', desc: 'Tipo de prazo' },
  { var: '{{days_remaining}}', desc: 'Dias restantes' },
  { var: '{{user_name}}', desc: 'Nome do utilizador' },
  { var: '{{tenant_name}}', desc: 'Nome da entidade' },
  { var: '{{portal_url}}', desc: 'URL do portal' }
];

const EmailTemplatesPage = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [showEditor, setShowEditor] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');

  const [formData, setFormData] = useState({
    template_type: 'CUSTOM',
    name: '',
    subject: '',
    html_content: ''
  });

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await adminApi.getEmailTemplates();
      setTemplates(response.data);
    } catch (err) {
      setError('Erro ao carregar templates');
    } finally {
      setLoading(false);
    }
  };

  const openEditor = (template = null) => {
    if (template) {
      setEditingTemplate(template);
      setFormData({
        template_type: template.template_type,
        name: template.name,
        subject: template.subject,
        html_content: template.html_content
      });
    } else {
      setEditingTemplate(null);
      setFormData({
        template_type: 'CUSTOM',
        name: '',
        subject: '',
        html_content: getDefaultTemplate()
      });
    }
    setShowEditor(true);
  };

  const getDefaultTemplate = () => {
    return `<!DOCTYPE html>
<html>
<head>
    <style>
        body { font-family: Arial, sans-serif; line-height: 1.6; color: #334155; }
        .container { max-width: 600px; margin: 0 auto; padding: 20px; }
        .header { background: #0F172A; color: white; padding: 20px; text-align: center; }
        .content { padding: 30px 20px; background: #f8fafc; }
        .footer { padding: 20px; text-align: center; font-size: 12px; color: #64748b; }
    </style>
</head>
<body>
    <div class="container">
        <div class="header">
            <h1>COGNIEtica</h1>
        </div>
        <div class="content">
            <h2>Titulo</h2>
            <p>Conteudo do email aqui...</p>
        </div>
        <div class="footer">
            <p>Este e um email automatico.</p>
        </div>
    </div>
</body>
</html>`;
  };

  const handleSave = async () => {
    if (!formData.name || !formData.subject || !formData.html_content) {
      setError('Preencha todos os campos obrigatorios');
      return;
    }

    setSaving(true);
    setError('');

    try {
      if (editingTemplate) {
        await adminApi.updateEmailTemplate(editingTemplate.id, formData);
        setSuccess('Template atualizado');
      } else {
        await adminApi.createEmailTemplate(formData);
        setSuccess('Template criado');
      }
      
      setShowEditor(false);
      fetchTemplates();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao guardar template');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (templateId) => {
    if (!window.confirm('Tem a certeza que pretende eliminar este template?')) return;
    
    try {
      await adminApi.deleteEmailTemplate(templateId);
      setSuccess('Template eliminado');
      fetchTemplates();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Erro ao eliminar template');
    }
  };

  const handlePreview = () => {
    // Replace variables with sample data
    let html = formData.html_content;
    html = html.replace(/\{\{tracking_code\}\}/g, 'DEN-2026-00123');
    html = html.replace(/\{\{status\}\}/g, 'EM_INVESTIGACAO');
    html = html.replace(/\{\{deadline_type\}\}/g, 'Feedback');
    html = html.replace(/\{\{days_remaining\}\}/g, '5');
    html = html.replace(/\{\{user_name\}\}/g, 'Joao Silva');
    html = html.replace(/\{\{tenant_name\}\}/g, 'COGNIEtica Demo');
    html = html.replace(/\{\{portal_url\}\}/g, 'https://exemplo.com/seguimento');
    
    setPreviewHtml(html);
    setShowPreview(true);
  };

  const insertVariable = (variable) => {
    setFormData(prev => ({
      ...prev,
      html_content: prev.html_content + variable
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>
            Templates de Email
          </h1>
          <p className="text-slate-500 mt-1">Personalize os emails enviados pelo sistema</p>
        </div>
        
        <Button onClick={() => openEditor()} data-testid="create-template-btn">
          <Plus className="w-4 h-4 mr-2" />
          Novo Template
        </Button>
      </div>

      {/* Alerts */}
      {error && (
        <Alert className="bg-red-50 border-red-200">
          <AlertCircle className="w-4 h-4 text-red-600" />
          <AlertDescription className="text-red-700">{error}</AlertDescription>
        </Alert>
      )}

      {success && (
        <Alert className="bg-emerald-50 border-emerald-200">
          <CheckCircle className="w-4 h-4 text-emerald-600" />
          <AlertDescription className="text-emerald-700">{success}</AlertDescription>
        </Alert>
      )}

      {/* Templates List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {templates.map((template) => (
          <Card key={template.id} className="hover:shadow-md transition-shadow">
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div>
                  <Badge className={TEMPLATE_TYPES[template.template_type]?.color || 'bg-slate-100'}>
                    {TEMPLATE_TYPES[template.template_type]?.label || template.template_type}
                  </Badge>
                  <CardTitle className="text-base mt-2">{template.name}</CardTitle>
                </div>
                <div className="flex items-center gap-1">
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => openEditor(template)}
                    data-testid={`edit-template-${template.id}`}
                  >
                    <Edit2 className="w-4 h-4" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => handleDelete(template.id)}
                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    data-testid={`delete-template-${template.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-slate-600 truncate">
                <strong>Assunto:</strong> {template.subject}
              </p>
              <p className="text-xs text-slate-400 mt-2">
                {template.is_active ? 'Ativo' : 'Inativo'}
              </p>
            </CardContent>
          </Card>
        ))}

        {templates.length === 0 && (
          <div className="col-span-full text-center py-12">
            <Mail className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">Nenhum template criado</p>
            <Button onClick={() => openEditor()} className="mt-4">
              Criar Primeiro Template
            </Button>
          </div>
        )}
      </div>

      {/* Editor Dialog */}
      <Dialog open={showEditor} onOpenChange={setShowEditor}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingTemplate ? 'Editar Template' : 'Novo Template'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="template_type">Tipo</Label>
                <Select 
                  value={formData.template_type}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, template_type: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(TEMPLATE_TYPES).map(([key, { label }]) => (
                      <SelectItem key={key} value={key}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="name">Nome</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Nome do template"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="subject">Assunto</Label>
              <Input
                id="subject"
                value={formData.subject}
                onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
                placeholder="[COGNIEtica] Assunto do email"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label htmlFor="html_content">Conteudo HTML</Label>
                <Button variant="outline" size="sm" onClick={handlePreview}>
                  <Eye className="w-4 h-4 mr-2" />
                  Pre-visualizar
                </Button>
              </div>
              <Textarea
                id="html_content"
                value={formData.html_content}
                onChange={(e) => setFormData(prev => ({ ...prev, html_content: e.target.value }))}
                className="font-mono text-sm min-h-[300px]"
                placeholder="<html>..."
              />
            </div>

            {/* Variables Helper */}
            <div className="bg-slate-50 rounded-lg p-4">
              <p className="text-sm font-medium text-slate-700 mb-2">Variaveis disponiveis:</p>
              <div className="flex flex-wrap gap-2">
                {AVAILABLE_VARIABLES.map(({ var: v, desc }) => (
                  <button
                    key={v}
                    onClick={() => insertVariable(v)}
                    className="text-xs px-2 py-1 bg-white border rounded hover:bg-slate-100 transition-colors"
                    title={desc}
                  >
                    {v}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditor(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  A guardar...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Guardar
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-3xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>Pre-visualizacao do Email</DialogTitle>
          </DialogHeader>
          <div className="border rounded-lg overflow-hidden">
            <iframe
              srcDoc={previewHtml}
              className="w-full h-[500px]"
              title="Email Preview"
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmailTemplatesPage;
